import React, { useState } from 'react';
import { Camera, Upload, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import api from '../utils/api';

const WardPhotoSubmit = ({ ward, slot = 'primary', onSubmitted }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error('Image must be under 8 MB'); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Please select an image'); return; }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('ward', ward);
      form.append('slot', slot);
      form.append('caption', caption);
      form.append('image', file);
      await api.post('/ward-photos/submit', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Submission received! It will be reviewed by our team.');
      setOpen(false);
      setFile(null);
      setPreview(null);
      setCaption('');
      onSubmitted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 text-[10px] font-mono text-gray-500 hover:text-[#C5A52A] transition-colors border border-gray-800 hover:border-[#C5A52A]/40 px-2 py-1 rounded"
        data-testid={`submit-ward-photo-${slot}`}
        title="Submit your photo for this slot"
      >
        <Camera className="w-3 h-3" /> Submit Photo
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-white">Submit a Photo</h3>
                <p className="text-xs text-gray-500 font-mono mt-0.5">
                  {ward.replace('-', ' ').toUpperCase()} · {slot} slot
                </p>
              </div>
              <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Image upload */}
              <div>
                {preview ? (
                  <div className="relative rounded overflow-hidden h-48">
                    <img src={preview} alt="Preview" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => { setFile(null); setPreview(null); }}
                      className="absolute top-2 right-2 bg-black/70 text-white p-1 rounded hover:bg-red-700 transition-colors">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-48 border-2 border-dashed border-gray-700 rounded cursor-pointer hover:border-[#C5A52A]/50 transition-colors">
                    <Upload className="w-8 h-8 text-gray-600 mb-2" />
                    <span className="text-gray-500 text-sm font-mono">Click to upload JPG, PNG, WebP</span>
                    <span className="text-gray-600 text-xs font-mono mt-1">Max 8 MB</span>
                    <input type="file" accept="image/*" onChange={handleFile} className="hidden" />
                  </label>
                )}
              </div>

              <div>
                <label className="block text-xs text-gray-400 uppercase tracking-wide mb-1">Caption (optional)</label>
                <input value={caption} onChange={e => setCaption(e.target.value)}
                  placeholder="What is this photo of?"
                  maxLength={140}
                  className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded text-sm" />
              </div>

              <p className="text-xs text-gray-600 font-mono">
                By submitting, you confirm this is your original photo. If selected as a winner, it will appear on the public ward page and you'll earn 50 points.
              </p>

              <div className="flex gap-3">
                <button type="submit" disabled={submitting || !file}
                  className="flex-1 bg-[#C5A52A] hover:bg-yellow-500 text-black font-mono text-sm py-2 rounded disabled:opacity-50 transition-colors">
                  {submitting ? 'Submitting...' : 'Submit Photo'}
                </button>
                <button type="button" onClick={() => setOpen(false)}
                  className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-300 font-mono text-sm py-2 rounded transition-colors">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default WardPhotoSubmit;
